import { call, delay, fork, getContext, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { TrackJS } from 'trackjs';
import { generateShortId } from 'utils/ids';
import { RootAction } from 'store/state';
import { mutate, resetMutationResponse } from 'store/state/mutationsResponse/actions';
import { SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import {
  setToastMessage,
  promotePrivateBulletin,
  setPrivateBulletinSuccesfullyPromotedModal,
  setPrivateBulletinSuccesfullyPromotedPopover,
  setPrivateBulletinHasAlreadyBeenPromotedModal,
  PromotePrivateBulletinViewSource,
  openAgentSupportModal,
  setPrivateBulletinSelectPromotionPlanModal,
  setPrivateBulletinPlanLoadingModal,
  upgradeUserPlanFlow,
  setAuthModal,
  setPrivateUserMonetizationEventsSource,
  redeemPromotionPlanGrant,
  cancelUserPromotionPlan,
  cancelSubscriptionThenEditListingStatus,
  setRedeemPromotionPlanGrantResultModal,
  setCancelUserPromotionPlanResultModal,
  setPrivateBulletinMinimumPromotionPlanModal,
  setHostedFieldsModalAction,
} from 'store/state/app/actions';
import {
  editCommercialListingResponseSelector,
  newCommercialListingResponseSelector,
  promotePrivateUserBulletinResponseSelector,
  redeemPhonePromotionPlanGrantResponseSelector,
  cancelUserPromotionPlanResponseSelector,
  editListingStatusResponseSelector,
  newListingResponseSelector,
} from 'store/state/mutationsResponse/selectors';
import { LOGOUT, SET_AFTER_UPLOAD_BULLETIN_MODAL_OPEN, TOGGLE_LOGIN_SUCCESS } from 'store/state/app/types';
import { SET_DOMAIN_DATA } from 'store/state/domainData/types';
import { resetDomainData, setDomainData } from 'store/state/domainData/actions';
import {
  isAgentManagerSelector,
  isAgentSelector,
  isConnectedUserSelector,
  isEditAllowedSelector,
  isPrivateUserSelector,
  isUserListingsLoadingSelector,
  isVisitorUserSelector,
  privateUserIdSelector,
  unitPageBulletinSelector,
  userListingsByStatusSelector,
} from 'store/state/domainData/selectors';
import {
  MANUAL_PROMOTE_PRIVATE_BULLETIN,
  LOAD_PLANS_DATA,
  UPGRADE_USER_PLAN_FLOW,
  REDEEM_PROMOTION_PLAN_GRANT,
  CANCEL_USER_PROMOTION_PLAN,
  CANCEL_SUBSCRIPTION_THEN_EDIT_LISTING_STATUS,
  SET_PRIVATE_BULLETIN_MINIMUM_PLAN_MODAL,
} from 'store/state/app/private-user-monetization/types';
import {
  currentPromotionPlanDomainDataSelector,
  makeIsBulletinBasicPromotionSelector,
  isPromotePrivateBulletinMutationLoadingSelector,
  latestManualPromotionsSelector,
  bulletinPromotionPlanSelector,
  activePromotionPlansDataSelector,
  isTranzilaHostedFieldsModeSelector,
} from 'store/state/app/private-user-monetization/selectors';
import { AgentSupportModalVariant, PrivateUserMonetizationSource } from 'store/state/app';
import { queryData } from './apiService';
import { LoadOptions, LoadType, MutationType, PromotionPlanInfo, PromotionPlanType, UserBulletinPromotionPlan, UserPromotionPlan } from './apiService/types';
import { INITIATE_SOURCE } from 'components/private-bulletin-promotion/types';
import { TranzilaModalType } from 'components/tranzila-hosted-fields/types';
import { BiDealType, BiEventCategory, biEventName, BiPromoteBulletinSource } from 'analytics/biTypes';
import { BulletinStatusType, DealType, IUserListingsByStatus } from 'utils/entities';
import { loginSuccessMutationPattern } from './userWatcher';
import { waitForUserResolve } from './routing/handlers/utils';
import { getRouteParams, prevRouteSelector, routeSelector } from 'store/state/selectors/router';
import { LocalStorage } from 'utils/localStorage';
import { PAYMENT_PROCESS_ID } from 'consts/localStorageKeys';
import { UserTokenStore } from 'helpers/userToken';
import ApolloClient, { NetworkStatus } from 'apollo-client';
import userCurrentPromotionPlanQuery from 'store/sagas/apiService/queries/userCurrentPromotionPlan.gql';
import { TRANSITION_SUCCESS } from 'store/state/router/types';
import { DecodedRouteParams, Route } from 'config/routes';
import { State as RouteState } from 'config/routes';
import { afterUploadBulletinModalStateSelector, privateUserMonetizationEventsSourceSelector } from 'store/state/app/selectors';
import { AuthenticationModalType } from 'components/authentication/types';
import { navigateTo } from 'store/state/router/actions';
import { editListingStatusAction } from 'store/state/uploadBulletin/actions';
import { makeVariantToEventsString } from './analytics/utils';
import { logAndTrackError } from 'helpers/jstracking';


export class FailedAttemptsStore {
  private readonly key: string = 'FAILED_ATTEMPTS';

  public get(): number {
    return LocalStorage.get(this.key) || 0;
  }
  public increment(): void {
    LocalStorage.set(this.key, this.get() + 1);
  }
  public reset(): void {
    LocalStorage.rm(this.key);
  }
}

const FAILED_ATTEMPTS_STORE = new FailedAttemptsStore();

const waitForMutationResponse = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.PromotePrivateUserBulletin
);

const waitForCancelUserPromotionPlanResponse = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.CancelUserPromotionPlan
);

const waitForEditListingStatusResponse = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.EditListingStatus
);

const waitForBulletinResponse = (action: RootAction) => (
  action.type === SET_DOMAIN_DATA
  && action.loadType === LoadType.UnitPageBulletin
);

const emailPlanRenewPattern = (action: RootAction) => (
  action.type === TRANSITION_SUCCESS && !!action.payload.route.params.emailRenewalPromotionPlanId
);

const dunningRenewalPattern = (action: RootAction) => (
  action.type === TRANSITION_SUCCESS
  && action.payload.route.name === Route.ManageBulletins
  && action.payload.route.params.source === 'promotionPlanDunningEmail'
  && !!action.payload.route.params.productId
  && !!action.payload.route.params.bulletinId
);

const updateCurrentPlanPattern = (action: RootAction) => (
  action.type === TRANSITION_SUCCESS
  && (action.payload.route.name === Route.UnitPage || action.payload.route.name === Route.ManageBulletins)
);

const currentPromotionPlanPattern = (action: RootAction) => (
  action.type === SET_DOMAIN_DATA
  && action.loadType === LoadType.UserCurrentPromotionPlan
);

const safeStringIncludes = (str: string, substr: string) => {
  try {
    return str.includes(substr);
  }
  catch (error) {
    return false;
  }
};

const viewSourceToAnalyticsSource: Record<PromotePrivateBulletinViewSource, BiPromoteBulletinSource> = {
  [PromotePrivateBulletinViewSource.ManageBulletinsPage]: BiPromoteBulletinSource.MANAGE_BULLETINS_PAGE,
  [PromotePrivateBulletinViewSource.BulletinPage]: BiPromoteBulletinSource.BULLETIN_PAGE,
};

function* handleManualPromotePrivateBulletin(action: ReturnType<typeof promotePrivateBulletin>) {
  const logger = yield getContext('logger');
  const { sendEvent } = yield getContext('analytics');
  const { bulletinId, bulletinType, viewSource, isTimerStillRunning, dealType } = action.payload || {};
  const isFreePlan = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));
  try {
    if (isTimerStillRunning) {
      if (isFreePlan) {
        yield put(setPrivateBulletinHasAlreadyBeenPromotedModal({ isOpen: true, bulletinId }));
      }
      else {
        yield put(setPrivateBulletinSuccesfullyPromotedPopover({ isOpen: true, isPaidAndRunning: true, bulletinId }));
      }
      return;
    }

    const isLoading: ReturnType<typeof isPromotePrivateBulletinMutationLoadingSelector> = yield select(isPromotePrivateBulletinMutationLoadingSelector);

    if (isLoading) return;

    yield put(
      mutate({
        mutationType: MutationType.PromotePrivateUserBulletin,
        meta: {
          variables: {
            input: {
              bulletinId,
              bulletinType: bulletinType === 'commercialBulletin' ? 'COMMERCIAL' : 'RESIDENTIAL',
              source: viewSource,
            },
          },
        },
      })
    );
    yield call(sendEvent, biEventName[BiEventCategory.BULLETIN_MANAGE].PROMOTE_MANUAL, BiEventCategory.BULLETIN_MANAGE, {
      event: {
        source: viewSourceToAnalyticsSource[viewSource] || 'unknown',
      },
      property: {
        deal_type: dealType === DealType.Buy ? BiDealType.BUY : BiDealType.RENT,
        property_id: bulletinId,
      },
    });

    yield take(waitForMutationResponse);
    const response: ReturnType<typeof promotePrivateUserBulletinResponseSelector> = yield select(promotePrivateUserBulletinResponseSelector);

    if (!response || !response.data) {
      // probably network error
      return;
    }

    if (response.data.promotePrivateUserBulletin.success) {
      const latestManualPromotions: ReturnType<typeof latestManualPromotionsSelector> = yield select(latestManualPromotionsSelector);
      const hasPromotedThisBulletinBefore = (latestManualPromotions || []).some(p => p.bulletinId === bulletinId);
      if (hasPromotedThisBulletinBefore || !isFreePlan) {
        yield put(setPrivateBulletinSuccesfullyPromotedPopover({ isOpen: true, bulletinId }));
      }
      else {
        yield put(setPrivateBulletinSuccesfullyPromotedModal({ isOpen: true, bulletinId }));
      }
      yield call(loadCurrentPromotionPlan);
    }
    else if (safeStringIncludes(response.data.promotePrivateUserBulletin.errorMessage, 'not allowed at this time')) {
      yield put(setPrivateBulletinHasAlreadyBeenPromotedModal({ isOpen: true, bulletinId }));
    }
    else if (safeStringIncludes(response.data.promotePrivateUserBulletin.errorMessage, 'does not have the required role')) {
      yield put(openAgentSupportModal({ variant: AgentSupportModalVariant.MONETIZATION }));
    }
    else {
      yield put(setToastMessage({ type: 'error', term: 'error.generic', autoHideTimeout: 5000 }));
    }
  }
  catch (error) {
    const correlationId = generateShortId();
    logger.error(`Failed to promote private bulletin: bulletinId=${bulletinId}, bulletinType=${bulletinType}, correlationId=${correlationId}.`, error);
    yield put(setToastMessage({
      type: 'error',
      preLineText: true,
      term: 'error.genericWithId',
      params: { correlationId },
      autoHideTimeout: 7000,
    }));
  }
}

const userSuccesfullyAuthenticatedPattern = (action: RootAction) => {
  return loginSuccessMutationPattern(action)
    || (action.type === SET_DOMAIN_DATA && action.loadType === LoadType.CurrentUser);
};

export function* loadCurrentPromotionPlan() {
  yield call(waitForUserResolve);
  const logger = yield getContext('logger');
  try {
    const isAgent: ReturnType<typeof isAgentSelector> = yield select(isAgentSelector);
    const isAgentManager: ReturnType<typeof isAgentManagerSelector> = yield select(isAgentManagerSelector);

    // NOTE: Currently, we can't use this optimization because of the workaround for the new investors login flow - "isAccountReady" (UserStatus.NATIVE_MISSING_PASSWORD) function in domainData/utils.ts
    // TODO: if we get rid of the workaround, we can use this optimization
    // const isConnectedUser: ReturnType<typeof isConnectedUserSelector> = yield select(isConnectedUserSelector);
    // if (!isConnectedUser || isAgent || isAgentManager) return;

    if (isAgent || isAgentManager) return;

    const loadOptions: LoadOptions<LoadType.UserCurrentPromotionPlan> = {
      loadType: LoadType.UserCurrentPromotionPlan,
      meta: { variables: { uiNonCache: Date.now().toString() } },
    };
    yield call(queryData, loadOptions);
  }
  catch (e) {
    const errorText = `Failed to load current promotion plan. Error: ${e.message}`;
    logger.error(errorText);
    TrackJS.track(errorText);
  }
}

export function* loadActivePlans(bulletinId: string | null) {
  const logger = yield getContext('logger');
  try {
    const isAgent: ReturnType<typeof isAgentSelector> = yield select(isAgentSelector);
    const isAgentManager: ReturnType<typeof isAgentManagerSelector> = yield select(isAgentManagerSelector);
    const isConnectedUser: ReturnType<typeof isConnectedUserSelector> = yield select(isConnectedUserSelector);

    if (!isConnectedUser || isAgent || isAgentManager) return;

    const loadOptions: LoadOptions<LoadType.ActivePromotionPlans> = {
      loadType: LoadType.ActivePromotionPlans,
      meta: {
        variables: {
          uiNonCache: Date.now().toString(),
          bulletinId,
          isNewGeneration: true,
        },
      },
    };
    yield call(queryData, loadOptions);
  }
  catch (e) {
    const errorText = `Failed to load active promotion plans. Error: ${e.message}`;
    logger.error(errorText);
    TrackJS.track(errorText);
  }
}

const activeMiniPromotionPlansModalClosed = (action: RootAction) => (
  action.type === SET_PRIVATE_BULLETIN_MINIMUM_PLAN_MODAL && !action.payload.isOpen
);

function* maybeOpenSuccessModal(action: ReturnType<typeof setPrivateBulletinMinimumPromotionPlanModal>) {
  const listingUploaded = yield select(newListingResponseSelector);
  if (listingUploaded && listingUploaded.data) {
    yield put(setPrivateUserMonetizationEventsSource(PrivateUserMonetizationSource.WizardUpload));
  }
  yield put(resetMutationResponse({ mutationType: MutationType.NewListing, key: null }));
  yield put(resetMutationResponse({ mutationType: MutationType.NewCommercialListing, key: null }));
}

function* logoutWorker() {
  yield put(resetDomainData({ loadType: LoadType.CurrentPromotionPlan }));
  yield put(resetDomainData({ loadType: LoadType.UserCurrentPromotionPlan }));
  yield put(resetDomainData({ loadType: LoadType.ActivePromotionPlans }));
}

function* upgrageUserPlanWorker(action: ReturnType<typeof upgradeUserPlanFlow>) {
  yield put(setPrivateBulletinPlanLoadingModal({ isOpen: true }));

  yield call(loadActivePlans, action.payload.bulletinId);
  yield call(loadCurrentPromotionPlan);

  yield put(setPrivateUserMonetizationEventsSource(action.payload.source));

  yield put(setPrivateBulletinPlanLoadingModal({ isOpen: false }));

  const bulletinId = action.payload.bulletinId;
  yield put(setPrivateBulletinSelectPromotionPlanModal({ isOpen: true, bulletinId }));
  const bulletinPromotionPlan: UserBulletinPromotionPlan | UserPromotionPlan = yield select(bulletinPromotionPlanSelector, bulletinId);
  const currentPlanType = bulletinPromotionPlan ? bulletinPromotionPlan.promotionPlan.type : null;

  if (action.payload.userInitiated) {
    const { sendEvent } = yield getContext('analytics');

    yield call(sendEvent, 'user_promotion_plan_upgrade_click', 'user', {
      event: {
        source: action.payload.source,
        promotion_plan: bulletinPromotionPlan.promotionPlan.variant ? makeVariantToEventsString(bulletinPromotionPlan.promotionPlan.variant) : undefined,
        deal_type: INITIATE_SOURCE.REGULAR,
        is_paying_already: currentPlanType ? currentPlanType !== PromotionPlanType.BASIC : undefined,
      },
    });
  }
}

const MAX_RECHECK_POLL_COUNT = 20;

function* recheckPlanWatcher(bulletinId: string) {
  const logger = yield getContext('logger');
  let pollCount = 0;
  try {
    while (pollCount < MAX_RECHECK_POLL_COUNT) {
      pollCount++;
      const client: ApolloClient<unknown> = yield getContext('client');
      const tokenStore: UserTokenStore = yield getContext('tokenStore');
      const userToken = tokenStore.get();

      const response = yield client.query({
        query: userCurrentPromotionPlanQuery,
        fetchPolicy: 'no-cache',
        variables: {
          userToken,
          uiNonCache: Date.now().toString(),
        },
      });

      if (response && response.data) {
        yield put(setDomainData({
          loadType: LoadType.UserCurrentPromotionPlan,
          payload: {
            networkStatus: NetworkStatus.ready,
            stale: false,
            data: response.data,
            loading: false,
          },
          meta: {
            variables: { uiNonCache: Date.now().toString() },
          },
        }));

        const isStillBasic: boolean = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));
        if (!isStillBasic) break;
      }

      yield delay(3000);
    }
    return true;
  }
  catch (e) {
    logger.error('Polling error:', e);
    return false;
  }
}

function* recheckPlan(key: string, bulletinId: string) {
  const result: boolean = yield call(recheckPlanWatcher, bulletinId);
  if (result === true) {
    LocalStorage.rm(key);
  }
}

function* freeToPaidPlanWatcher() {
  yield take(currentPromotionPlanPattern);
  yield call(waitForUserResolve);

  const userId: string = yield select(privateUserIdSelector);
  if (!userId) return;

  const prefix = `${PAYMENT_PROCESS_ID}_${userId}_`;
  const allKeys: string[] = LocalStorage.keys() || [];
  const existingKey: string | undefined = allKeys.find(k => k.startsWith(prefix));

  if (existingKey) {
    const bulletinId = existingKey.slice(prefix.length);
    const isBasicPlan: boolean = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));
    if (isBasicPlan) {
      yield call(recheckPlan, existingKey, bulletinId);
    }
    else {
      LocalStorage.rm(existingKey);
    }
    return;
  }

  const route = yield select(routeSelector);
  const uuk: string | undefined = route.params.uuk;
  if (route.params.payment_outcome === 'success' && uuk && uuk.startsWith(prefix)) {
    const bulletinId = uuk.slice(prefix.length);
    const isBasicPlan: boolean = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));
    if (isBasicPlan) {
      LocalStorage.set(uuk, '1');
      yield call(recheckPlan, uuk, bulletinId);
    }
  }
}

function* needToRefetchCurrentPlan() {
  yield take(waitForBulletinResponse);
  yield call(waitForUserResolve);
  const currentRoute: RouteState = yield select(routeSelector);
  const prevRoute: RouteState = yield select(prevRouteSelector);
  const isPrivateUser: boolean = yield select(isPrivateUserSelector);
  const isEditEnabled: boolean = yield select(isEditAllowedSelector);
  const unitPageBulletin = yield select(unitPageBulletinSelector);
  const isBasicPlan: boolean = yield select(makeIsBulletinBasicPromotionSelector(unitPageBulletin && unitPageBulletin.id));

  if (isPrivateUser && ((currentRoute.name === Route.ManageBulletins && prevRoute) || (currentRoute.name === Route.UnitPage && isEditEnabled && !isBasicPlan))) {
    yield call(loadCurrentPromotionPlan);
  }
}

function* uploadEditBulletinEventSourceWatcher() {
  const commercialUploaded = yield select(newCommercialListingResponseSelector);
  const commercialEdited = yield select(editCommercialListingResponseSelector);
  const afterUploadModal = yield select(afterUploadBulletinModalStateSelector);
  const bulletinId: string | null = afterUploadModal && afterUploadModal.poi && afterUploadModal.poi.id;
  const isFreePlan: boolean = yield select(makeIsBulletinBasicPromotionSelector(bulletinId));

  if (isFreePlan && !(commercialUploaded && commercialUploaded.data) && !(commercialEdited && commercialEdited.data) && afterUploadModal) {
    const source = afterUploadModal.uploadType === 'UPLOAD' ? PrivateUserMonetizationSource.WizardUpload : PrivateUserMonetizationSource.WizardEdit;
    yield put(setPrivateUserMonetizationEventsSource(source));
  }
}

const setListingsPattern = (action: RootAction) => (
  action.type === SET_DOMAIN_DATA
  && action.loadType === LoadType.UserAllListings
);
export function* waitForListingsResolve() {
  const isLoading: boolean = yield select(isUserListingsLoadingSelector);

  if (isLoading) {
    yield take(setListingsPattern);
  }
  return true;
}

function* loadPlansDataWorker() {
  yield call(loadActivePlans, null);
  yield call(loadCurrentPromotionPlan);
}

function* emailRenewPlanWatcher() {

  yield put(navigateTo(Route.ManageBulletins, { emailRenewalPromotionPlanId: undefined }, { replace: true }));
  yield call(waitForUserResolve);

  const isVisitor = yield select(isVisitorUserSelector);

  if (isVisitor) {
    yield put(setAuthModal({ type: AuthenticationModalType.SignUp }, { isUserInitiated: false }));
    yield take(TOGGLE_LOGIN_SUCCESS);
  }

  const isPrivateUser = yield select(isPrivateUserSelector);

  if (isPrivateUser) {
    yield take(currentPromotionPlanPattern);
    yield call(loadActivePlans, null);
    yield call(waitForListingsResolve);
    const currentPlan: UserPromotionPlan = yield select(currentPromotionPlanDomainDataSelector);

    if (currentPlan.promotionPlan.type !== PromotionPlanType.BASIC) return;

    const listingsByStatus: IUserListingsByStatus = yield select(userListingsByStatusSelector);
    const displayedBulletins = listingsByStatus && listingsByStatus[BulletinStatusType.Displayed] || [];
    const activeBulletins = displayedBulletins.filter(b => b.type === 'bulletin');

    yield put(setPrivateUserMonetizationEventsSource(PrivateUserMonetizationSource.Email));

    const emailFlowBulletinId = activeBulletins.length ? activeBulletins[0].id : null;

    if (emailFlowBulletinId) {
      yield put(setPrivateBulletinSelectPromotionPlanModal({ isOpen: true, bulletinId: emailFlowBulletinId }));
    }
  }
}

function* dunningRenewalWatcher(action: RootAction) {
  const logger = yield getContext('logger');
  try {
    if (action.type !== TRANSITION_SUCCESS) return; // for type safety

    const { bulletinId, productId } = action.payload.route.params;

    const isHostedFieldsMode: boolean = yield select(isTranzilaHostedFieldsModeSelector);
    if (!isHostedFieldsMode) {
      logger.info('promotionPlanDunning: hosted fields AB test is off, skipping payment modal');
      return;
    }

    yield call(waitForListingsResolve);
    yield call(waitForUserResolve);

    yield put(navigateTo(Route.ManageBulletins, { source: undefined, bulletinId: undefined, productId: undefined }, { replace: true }));

    const isPrivateUser: boolean = yield select(isPrivateUserSelector);
    if (!isPrivateUser) return;

    const listingsByStatus: IUserListingsByStatus = yield select(userListingsByStatusSelector);
    const displayedBulletins = (listingsByStatus && listingsByStatus[BulletinStatusType.Displayed]) || [];
    const isBulletinActive = displayedBulletins.some(b => b.id === bulletinId);

    if (!isBulletinActive) {
      yield put(setToastMessage({ type: 'error', term: 'promotionPlanDunning.error.bulletinNotActive', autoHideTimeout: 5000 }));
      return;
    }

    yield call(loadActivePlans, bulletinId);

    const activePlans: PromotionPlanInfo[] | null = yield select(activePromotionPlansDataSelector);
    const matchingPlan = activePlans && activePlans.find(p => p.id === productId);

    if (!matchingPlan) {
      logAndTrackError(`Dunning renewal: plan not active, productId=${productId}`);
      yield put(setToastMessage({ type: 'error', term: 'promotionPlanDunning.error.planNotActive', autoHideTimeout: 5000 }));
      return;
    }

    yield call(loadCurrentPromotionPlan);

    const bulletinPlan: ReturnType<typeof bulletinPromotionPlanSelector> = yield select(state => bulletinPromotionPlanSelector(state, bulletinId));
    if (bulletinPlan && bulletinPlan.promotionPlan.type !== PromotionPlanType.BASIC) {
      logger.info(`promotionPlanDunning: bulletin ${bulletinId} already has an active plan, skipping`);
      return;
    }

    yield put(setPrivateUserMonetizationEventsSource(PrivateUserMonetizationSource.Email));

    yield put(setHostedFieldsModalAction({
      planInfo: matchingPlan,
      bulletinId,
      modalType: TranzilaModalType.Loading,
      paymentDealType: 'promotion_plan_renewal',
    }));
  }
  catch (error) {
    logAndTrackError('Failed to handle dunning renewal', error);
  }
}

function* handleRedeemPromotionPlanGrant(action: ReturnType<typeof redeemPromotionPlanGrant>) {
  const logger = yield getContext('logger');
  const { sendEvent } = yield getContext('analytics');
  try {
    const monetizationSource = yield select(privateUserMonetizationEventsSourceSelector);
    const routeParams: DecodedRouteParams = yield select(getRouteParams);
    const eventSource = monetizationSource || (routeParams ? routeParams.source : undefined);
    yield call(sendEvent, 'user_promotion_plan_select', 'user', {
      event: {
        deal_type: INITIATE_SOURCE.REGULAR,
        source: eventSource,
        promotion_plan: 'settle_package_26',
      },
    });

    yield put(mutate({
      mutationType: MutationType.RedeemPhonePromotionPlanGrant,
      meta: {
        variables: {
          phone: action.payload.phone,
        },
      },
    }));

    yield take((a: RootAction) => a.type === SET_MUTATION_RESPONSE && a.mutationType === MutationType.RedeemPhonePromotionPlanGrant);

    const response: ReturnType<typeof redeemPhonePromotionPlanGrantResponseSelector> = yield select(redeemPhonePromotionPlanGrantResponseSelector);

    const isGranted = response && response.data && response.data.redeemPromotionPlanGrant && response.data.redeemPromotionPlanGrant.success;
    if (!isGranted) {
      FAILED_ATTEMPTS_STORE.increment();
      const errorCode = response.data && response.data.redeemPromotionPlanGrant && response.data.redeemPromotionPlanGrant.errorCode;
      logger.error(`Failed to redeem promotion plan grant: errorCode=${errorCode}`);
      yield put(setToastMessage({ type: 'error', term: 'error.generic', autoHideTimeout: 5000 }));
      yield call(sendEvent, 'user_promotion_plan_submit_failed', 'user', {
        event: {
          deal_type: INITIATE_SOURCE.REGULAR,
          source: eventSource,
          promotion_plan: 'settle_package_26',
          transaction_error_code: errorCode,
          dunning_attempt_number: Math.min(FAILED_ATTEMPTS_STORE.get(), 4),
        },
      });
      return;
    }

    // Reload the current promotion plan to reflect the change
    yield call(loadCurrentPromotionPlan);
    yield put(setRedeemPromotionPlanGrantResultModal({ isOpen: true }));

    yield call(sendEvent, 'user_promotion_plan_submit_success', 'user', {
      event: {
        deal_type: INITIATE_SOURCE.REGULAR,
        source: eventSource,
        promotion_plan: 'settle_package_26',
        dunning_attempt_number: Math.min(FAILED_ATTEMPTS_STORE.get(), 4),
        amount_ils: 0,
      },
    });
    FAILED_ATTEMPTS_STORE.reset();
  }
  catch (error) {
    logger.error(`Failed to redeem promotion plan grant`, error);
    yield put(setToastMessage({
      type: 'error',
      preLineText: true,
      term: 'error.generic',
      params: {},
      autoHideTimeout: 7000,
    }));
  }
}

function* handleCancelUserPromotionPlan(action: ReturnType<typeof cancelUserPromotionPlan>) {
  const logger = yield getContext('logger');
  try {
    yield put(mutate({
      mutationType: MutationType.CancelUserPromotionPlan,
      meta: {
        variables: {
          userPromotionPlanId: action.payload.userPromotionPlanId,
          reason: action.payload.reason,
        },
      },
    }));

    yield take(waitForCancelUserPromotionPlanResponse);

    const response: ReturnType<typeof cancelUserPromotionPlanResponseSelector> = yield select(cancelUserPromotionPlanResponseSelector);
    const isSuccess = response
      && response.data
      && response.data.cancelUserPromotionPlan
      && response.data.cancelUserPromotionPlan.success;

    if (!isSuccess) {
      logger.error('Failed to cancel user promotion plan', response && response.data && response.data.cancelUserPromotionPlan);
      yield put(setToastMessage({ type: 'error', term: 'error.generic', autoHideTimeout: 5000 }));
      return;
    }

    yield call(loadCurrentPromotionPlan);
    yield put(setCancelUserPromotionPlanResultModal({ isOpen: true }));
  }
  catch (error) {
    logger.error('Failed to cancel user promotion plan', error);
    yield put(setToastMessage({
      type: 'error',
      preLineText: true,
      term: 'error.generic',
      params: {},
      autoHideTimeout: 7000,
    }));
  }
}

function* handleCancelSubscriptionThenEditListingStatus(action: ReturnType<typeof cancelSubscriptionThenEditListingStatus>) {
  const logger = yield getContext('logger');
  try {
    const { userPromotionPlanId, editListingStatus } = action.payload;

    yield put(mutate({
      mutationType: MutationType.CancelUserPromotionPlan,
      meta: {
        variables: {
          userPromotionPlanId,
        },
      },
    }));

    yield take(waitForCancelUserPromotionPlanResponse);

    const cancelResponse: ReturnType<typeof cancelUserPromotionPlanResponseSelector> = yield select(cancelUserPromotionPlanResponseSelector);
    const cancelSuccess = cancelResponse
      && cancelResponse.data
      && cancelResponse.data.cancelUserPromotionPlan
      && cancelResponse.data.cancelUserPromotionPlan.success;

    if (!cancelSuccess) {
      logger.error('Failed to cancel user promotion plan (edit listing after cancel flow)', cancelResponse && cancelResponse.data && cancelResponse.data.cancelUserPromotionPlan);
      yield put(setToastMessage({ type: 'error', term: 'error.generic', autoHideTimeout: 5000 }));
      return;
    }

    yield call(loadCurrentPromotionPlan);

    yield put(editListingStatusAction({ ...editListingStatus, isSubscription: true }));

    yield take(waitForEditListingStatusResponse);

    const editResponse: ReturnType<typeof editListingStatusResponseSelector> = yield select(editListingStatusResponseSelector);
    const editData = editResponse && editResponse.data && editResponse.data.editListingStatus;
    const editSuccess = Boolean(editData && editData.listing);

    if (!editSuccess) {
      logger.error('Failed to change listing status after subscription cancel', editResponse && editResponse.data && editResponse.data.editListingStatus);
      yield put(setCancelUserPromotionPlanResultModal({ isOpen: true }));
      yield put(setToastMessage({
        type: 'error',
        preLineText: true,
        term: 'error.updateFailed',
        params: {},
        autoHideTimeout: 7000,
      }));
      return;
    }

    yield call(loadCurrentPromotionPlan);
    yield put(setCancelUserPromotionPlanResultModal({ isOpen: true }));
  }
  catch (error) {
    logger.error('Failed to cancel subscription and change listing status', error);
    yield put(setToastMessage({
      type: 'error',
      preLineText: true,
      term: 'error.generic',
      params: {},
      autoHideTimeout: 7000,
    }));
  }
}

export function* privateUserMonetizationWatcher() {
  yield takeLeading(MANUAL_PROMOTE_PRIVATE_BULLETIN, handleManualPromotePrivateBulletin);
  yield takeEvery(SET_AFTER_UPLOAD_BULLETIN_MODAL_OPEN, uploadEditBulletinEventSourceWatcher);
  yield takeLatest(userSuccesfullyAuthenticatedPattern, loadCurrentPromotionPlan);
  yield takeLatest(activeMiniPromotionPlansModalClosed, maybeOpenSuccessModal);
  yield takeLatest(UPGRADE_USER_PLAN_FLOW, upgrageUserPlanWorker);
  yield takeEvery(LOGOUT, logoutWorker);
  yield takeEvery(updateCurrentPlanPattern, needToRefetchCurrentPlan);
  yield fork(freeToPaidPlanWatcher);
  yield takeEvery(emailPlanRenewPattern, emailRenewPlanWatcher); // Can be removed in a month after we release the new promotion plans
  yield takeLeading(dunningRenewalPattern, dunningRenewalWatcher);
  yield takeEvery(LOAD_PLANS_DATA, loadPlansDataWorker);
  yield takeLatest(REDEEM_PROMOTION_PLAN_GRANT, handleRedeemPromotionPlanGrant);
  yield takeLatest(CANCEL_USER_PROMOTION_PLAN, handleCancelUserPromotionPlan);
  yield takeLeading(CANCEL_SUBSCRIPTION_THEN_EDIT_LISTING_STATUS, handleCancelSubscriptionThenEditListingStatus);
}
